/* eslint-disable max-lines */
import wretch from 'wretch';
import { useQuery, queryOptions, UseQueryOptions } from '@tanstack/react-query';

import { useIsAuthenticated, useIsTokenExpired } from '../auth/user-info.hooks';
import { useRefreshAccessToken } from '../auth/useRefreshAccessToken';
import { useApiHeaders, ExtraHeaders } from './useApiHeaders';
import { useApiBase } from './useApiBase';

type ObjectMap = {
  [key: string]: any;
};

type ExtraQueryOptions = {
  isEncodedParams?: boolean;
  isLogoutApi?: boolean;
};

const apiWretcher = wretch(undefined).options({
  credentials: 'same-origin',
  mode: 'cors',
});

export function useApiQuery<TO = ObjectMap>(
  route: string,
  params?: ObjectMap,
  useQueryOptions?: Partial<UseQueryOptions<TO>>,
  extraHeaders?: ExtraHeaders,
  extraQueryOptions?: ExtraQueryOptions
) {
  const isAuthenticated = useIsAuthenticated();
  const isTokenExpired = useIsTokenExpired();
  const apiGet = useApiGet<TO>(extraQueryOptions);

  // Wait for authorization
  const enabled =
    ((!isTokenExpired && isAuthenticated) ||
      !!extraQueryOptions?.isLogoutApi) &&
    (useQueryOptions?.enabled || useQueryOptions?.enabled === undefined);

  const contentOptions = () => {
    return queryOptions<TO>({
      enabled,
      queryFn: () => apiGet(route, params, extraHeaders),
      queryKey: [route, params, extraHeaders],
      retry: 0,
    });
  };

  return useQuery<TO>({ ...useQueryOptions, ...contentOptions() });
}

export function useApiGet<TO = ObjectMap>(
  extraQueryOptions?: ExtraQueryOptions
) {
  const isLogoutQuery = !!extraQueryOptions?.isLogoutApi;
  const isEncodedParams =
    extraQueryOptions?.isEncodedParams &&
    localStorage.getItem('useUrlEncoding') === 'true';
  const getBaseUrl = useApiBase();
  const getApiHeaders = useApiHeaders(isLogoutQuery, isEncodedParams);
  const refreshAccessToken = useRefreshAccessToken();

  return function (
    route: string,
    params?: ObjectMap,
    extraHeaders?: ExtraHeaders
  ) {
    const apiHeaders = getApiHeaders(extraHeaders);
    const apiBase = getBaseUrl(route);
    const url = `${apiBase}${route}`;

    let apiCall = apiWretcher.url(url);
    if (params) {
      apiCall = apiCall.query(params);
    }
    return apiCall
      .headers(apiHeaders)
      .get()
      .json<TO>()
      .catch((error) => {
        if (error?.response?.status === 401 && !isLogoutQuery) {
          return refreshAccessToken().then((success) => {
            if (success) {
              //repeating API call
              return apiCall
                .headers(getApiHeaders(extraHeaders))
                .get()
                .json<TO>()
                .catch((err) => {
                  throw err;
                });
            } else {
              throw error;
            }
          });
        } else {
          throw error;
        }
      });
  };
}

export function useApiPost<TO = ObjectMap>( //FIXME
  extraQueryOptions?: ExtraQueryOptions
) {
  const isLogoutQuery = !!extraQueryOptions?.isLogoutApi;
  // const isEncodedParams =
  //   extraQueryOptions?.isEncodedParams &&
  //   localStorage.getItem('useUrlEncoding') === 'true';
  const getBaseUrl = useApiBase();
  const getApiHeaders = useApiHeaders(isLogoutQuery);
  const refreshAccessToken = useRefreshAccessToken();

  return function (
    route: string,
    body: ObjectMap,
    params?: ObjectMap,
    extraHeaders?: ExtraHeaders
  ) {
    const url = `${getBaseUrl(route)}${route}`;

    let apiCall = apiWretcher.url(url);
    if (params) {
      apiCall = apiCall.query(params);
    }
    return apiCall
      .headers(getApiHeaders(extraHeaders))
      .body(JSON.stringify(body))
      .post()
      .json<TO>()
      .catch((error) => {
        if (error?.response?.status === 401 && !isLogoutQuery) {
          return refreshAccessToken().then((success) => {
            if (success) {
              //repeating API call
              return apiCall
                .headers(getApiHeaders(extraHeaders))
                .body(JSON.stringify(body))
                .post()
                .json<TO>()
                .catch((err) => {
                  throw err;
                });
            } else {
              throw error;
            }
          });
        } else {
          throw error;
        }
      });
  };
}

export function useApiPostFormData<TO = ObjectMap>(
  extraQueryOptions?: ExtraQueryOptions
) {
  const isLogoutQuery = !!extraQueryOptions?.isLogoutApi;
  const getBaseUrl = useApiBase();
  const getApiHeaders = useApiHeaders(isLogoutQuery);
  const refreshAccessToken = useRefreshAccessToken();

  return function (
    route: string,
    formData: ObjectMap,
    params?: ObjectMap,
    extraHeaders?: ExtraHeaders
  ) {
    const apiheaders = getApiHeaders(extraHeaders);
    delete apiheaders['content-type']; //The browser has to infer the content-type automatically for multipart requests.

    const url = `${getBaseUrl(route)}${route}`;

    let apiCall = apiWretcher.url(url);
    if (params) {
      apiCall = apiCall.query(params);
    }
    return apiCall
      .headers(apiheaders)
      .formData(formData)
      .post()
      .json<TO>()
      .catch((error) => {
        if (error?.response?.status === 401 && !isLogoutQuery) {
          return refreshAccessToken().then((success) => {
            if (success) {
              //repeating API call
              return apiCall
                .headers(getApiHeaders(extraHeaders))
                .formData(formData)
                .post()
                .json<TO>()
                .catch((err) => {
                  throw err;
                });
            } else {
              throw error;
            }
          });
        } else {
          throw error;
        }
      });
  };
}

export function useApiPatch<TO = ObjectMap>() {
  const getBaseUrl = useApiBase();
  const getApiHeaders = useApiHeaders();
  const refreshAccessToken = useRefreshAccessToken();

  return function (
    route: string,
    body: ObjectMap,
    params?: ObjectMap,
    extraHeaders?: ExtraHeaders
  ) {
    const url = `${getBaseUrl(route)}${route}`;

    let apiCall = apiWretcher.url(url);
    if (params) {
      apiCall = apiCall.query(params);
    }
    return apiCall
      .headers(getApiHeaders(extraHeaders))
      .body(JSON.stringify(body))
      .patch()
      .json<TO>()
      .catch((error) => {
        if (error?.response?.status === 401) {
          return refreshAccessToken().then((success) => {
            if (success) {
              //repeating API call
              return apiCall
                .headers(getApiHeaders(extraHeaders))
                .body(JSON.stringify(body))
                .patch()
                .json<TO>()
                .catch((err) => {
                  throw err;
                });
            } else {
              throw error;
            }
          });
        } else {
          throw error;
        }
      });
  };
}

export function useApiDelete<TO = ObjectMap>() {
  const getBaseUrl = useApiBase();
  const getApiHeaders = useApiHeaders();
  const refreshAccessToken = useRefreshAccessToken();

  return function (
    route: string,
    body?: ObjectMap,
    params?: ObjectMap,
    extraHeaders?: ExtraHeaders
  ) {
    const url = `${getBaseUrl(route)}${route}`;

    let apiCall = apiWretcher.url(url);
    if (body) {
      apiCall = apiCall.body(JSON.stringify(body));
    }
    if (params) {
      apiCall = apiCall.query(params);
    }
    return apiCall
      .headers(getApiHeaders(extraHeaders))
      .delete()
      .json<TO>()
      .catch((error) => {
        if (error?.response?.status === 401) {
          return refreshAccessToken().then((success) => {
            if (success) {
              //repeating API call
              return apiCall
                .headers(getApiHeaders(extraHeaders))
                .delete()
                .json<TO>()
                .catch((err) => {
                  throw err;
                });
            } else {
              throw error;
            }
          });
        } else {
          throw error;
        }
      });
  };
}

export function useApiPut<TO = ObjectMap>(
  extraQueryOptions?: ExtraQueryOptions
) {
  // const isEncodedParams =
  //   extraQueryOptions?.isEncodedParams &&
  //   localStorage.getItem('useUrlEncoding') === 'true';
  const getBaseUrl = useApiBase();
  const getApiHeaders = useApiHeaders();
  const refreshAccessToken = useRefreshAccessToken();

  return function (
    route: string,
    body: ObjectMap,
    params?: ObjectMap,
    extraHeaders?: ExtraHeaders
  ) {
    const url = `${getBaseUrl(route)}${route}`;

    let apiCall = apiWretcher.url(url);
    if (body) {
      apiCall = apiCall.body(JSON.stringify(body));
    }
    if (params) {
      apiCall = apiCall.query(params);
    }
    return apiCall
      .headers(getApiHeaders(extraHeaders))
      .body(JSON.stringify(body))
      .put()
      .json<TO>()
      .catch((error) => {
        if (error?.response?.status === 401) {
          return refreshAccessToken().then((success) => {
            if (success) {
              //repeating API call
              return apiCall
                .headers(getApiHeaders(extraHeaders))
                .body(JSON.stringify(body))
                .put()
                .json<TO>()
                .catch((err) => {
                  throw err;
                });
            } else {
              throw error;
            }
          });
        } else {
          throw error;
        }
      });
  };
}

/**
 * WARNING: USE react-query `useMutation` for true POST apis that MUTATE data.
 *
 * This `useApiPostQuery` is only for apis that retrieve data and
 * DO NOT MUTATE data.
 * These apis should have been implemented with GET method
 * but currently use POST method instead.
 */
// eslint-disable-next-line max-params
export function useApiPostQuery<TO = ObjectMap>(
  route: string,
  body: ObjectMap,
  params?: ObjectMap,
  useQueryOptions?: Partial<UseQueryOptions<TO>>,
  extraHeaders?: ExtraHeaders,
  extraQueryOptions?: ExtraQueryOptions
) {
  const isAuthenticated = useIsAuthenticated();
  const apiPost = useApiPost<TO>(extraQueryOptions);

  // Wait for authorization
  const enabled =
    (isAuthenticated || !!extraQueryOptions?.isLogoutApi) &&
    (useQueryOptions?.enabled || useQueryOptions?.enabled === undefined);

  const contentOptions = () => {
    return queryOptions({
      enabled,
      queryFn: () => apiPost(route, body, params, extraHeaders),
      queryKey: [route, body, params, extraHeaders],
      retry: 0,
    });
  };

  return useQuery(contentOptions());
}
